import { useCallback, useEffect, useRef, useState } from 'react';

const getSpeechRecognition = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

/** Errors that fire during normal stop/cleanup — not shown to users. */
const SILENT_ERRORS = new Set(['aborted']);
const MAX_NETWORK_RETRIES = 2;

function isBrowserOnline() {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

function mapSpeechError(code) {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission denied. Allow microphone access in your browser settings and try again.';
    case 'no-speech':
      return 'No speech detected. Please speak clearly and try again.';
    case 'network':
      return isBrowserOnline()
        ? 'Voice recognition is temporarily unavailable. Please try again.'
        : 'You appear to be offline. Connect to the internet and try again.';
    case 'audio-capture':
      return 'No microphone was found. Please connect a microphone and try again.';
    default:
      return null;
  }
}

/**
 * Web Speech API hook for voice search (modal-friendly).
 * @returns {{ supported, unsupportedReason, status, errorMessage, interimTranscript, beginSession, endSession, retry }}
 */
export default function useVoiceSearch({ onResult, onError, lang = 'en-IN' } = {}) {
  const recognitionRef = useRef(null);
  const completedRef = useRef(false);
  const processingRef = useRef(false);
  const sessionActiveRef = useRef(false);
  const tearingDownRef = useRef(false);
  const networkRetryRef = useRef(0);
  const handlersRef = useRef({ onResult, onError });
  const langRef = useRef(lang);
  const launchRecognitionRef = useRef(() => {});

  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [supported, setSupported] = useState(false);
  const [unsupportedReason, setUnsupportedReason] = useState('');

  useEffect(() => {
    handlersRef.current = { onResult, onError };
  }, [onResult, onError]);

  useEffect(() => {
    langRef.current = lang;
  }, [lang]);

  useEffect(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!window.isSecureContext) {
      setSupported(false);
      setUnsupportedReason('Voice search requires a secure connection (HTTPS).');
      return undefined;
    }
    if (!SpeechRecognition) {
      setSupported(false);
      setUnsupportedReason('Voice search is not supported in this browser. Try Chrome or Edge.');
      return undefined;
    }
    setSupported(true);
    setUnsupportedReason('');

    return () => {
      sessionActiveRef.current = false;
      tearingDownRef.current = true;
      try {
        recognitionRef.current?.abort();
      } catch (_) { /* ignore */ }
      recognitionRef.current = null;
    };
  }, []);

  const stopRecognition = useCallback(() => {
    tearingDownRef.current = true;
    try {
      recognitionRef.current?.abort();
    } catch (_) { /* ignore */ }
    recognitionRef.current = null;
    queueMicrotask(() => {
      tearingDownRef.current = false;
    });
  }, []);

  const fail = useCallback((message) => {
    if (!sessionActiveRef.current || completedRef.current) return;
    sessionActiveRef.current = false;
    stopRecognition();
    setStatus('error');
    setErrorMessage(message);
    handlersRef.current.onError?.(message);
  }, [stopRecognition]);

  const launchRecognition = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition || !sessionActiveRef.current) return;

    if (recognitionRef.current) {
      tearingDownRef.current = true;
      try {
        recognitionRef.current.abort();
      } catch (_) { /* ignore */ }
      recognitionRef.current = null;
      tearingDownRef.current = false;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = langRef.current;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onstart = () => {
      if (!sessionActiveRef.current) return;
      networkRetryRef.current = 0;
      setStatus('listening');
    };

    recognition.onresult = (event) => {
      if (!sessionActiveRef.current) return;
      let interim = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const piece = event.results[i][0]?.transcript || '';
        if (event.results[i].isFinal) finalText += piece;
        else interim += piece;
      }
      setInterimTranscript(interim.trim());
      const trimmed = finalText.trim();
      if (trimmed) {
        completedRef.current = true;
        processingRef.current = true;
        sessionActiveRef.current = false;
        setStatus('processing');
        setInterimTranscript(trimmed);
        handlersRef.current.onResult?.(trimmed);
        try {
          recognition.stop();
        } catch (_) { /* ignore */ }
      }
    };

    recognition.onerror = (event) => {
      if (tearingDownRef.current || completedRef.current || !sessionActiveRef.current) return;
      if (SILENT_ERRORS.has(event.error)) return;

      if (event.error === 'network' && isBrowserOnline() && networkRetryRef.current < MAX_NETWORK_RETRIES) {
        networkRetryRef.current += 1;
        recognitionRef.current = null;
        try {
          recognition.stop();
        } catch (_) { /* ignore */ }
        window.setTimeout(() => {
          if (!sessionActiveRef.current || completedRef.current) return;
          launchRecognitionRef.current();
        }, 400);
        return;
      }

      const mapped = mapSpeechError(event.error);
      if (mapped) {
        fail(mapped);
        return;
      }
      if (event.error) {
        fail('Voice recognition failed. Please try again.');
      }
    };

    recognition.onend = () => {
      if (tearingDownRef.current || !sessionActiveRef.current) return;
      if (completedRef.current || processingRef.current) {
        sessionActiveRef.current = false;
        return;
      }
      fail('No speech detected. Please try again.');
    };

    recognitionRef.current = recognition;
    setStatus('listening');

    try {
      recognition.start();
    } catch (err) {
      const msg = err?.name === 'InvalidStateError'
        ? 'Voice search is already active. Please wait a moment and try again.'
        : 'Could not start voice recognition. Please try again.';
      fail(msg);
    }
  }, [fail]);

  useEffect(() => {
    launchRecognitionRef.current = launchRecognition;
  }, [launchRecognition]);

  const endSession = useCallback(() => {
    sessionActiveRef.current = false;
    completedRef.current = true;
    processingRef.current = false;
    networkRetryRef.current = 0;
    stopRecognition();
    setStatus('idle');
    setInterimTranscript('');
    setErrorMessage('');
  }, [stopRecognition]);

  const beginSession = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!window.isSecureContext) {
      fail('Voice search requires a secure connection (HTTPS).');
      return;
    }
    if (!SpeechRecognition) {
      fail('Voice search is not supported in this browser.');
      return;
    }

    completedRef.current = false;
    processingRef.current = false;
    networkRetryRef.current = 0;
    sessionActiveRef.current = true;
    setErrorMessage('');
    setInterimTranscript('');
    setStatus('requesting_permission');
    launchRecognition();
  }, [fail, launchRecognition]);

  const retry = useCallback(() => {
    beginSession();
  }, [beginSession]);

  return {
    supported,
    unsupportedReason,
    status,
    errorMessage,
    interimTranscript,
    beginSession,
    endSession,
    retry,
  };
};
